import { useEffect, useMemo, useState, type ReactNode } from "react";
import { AnchorLink, isPathActive, type ShellFocusArea, type ShellLink } from "./types";

export function FocusSidebar({
    brand,
    areas,
    pathname,
    footer,
    onNavigate,
    link: Link = AnchorLink,
    switcher = true,
    lead,
}: {
    brand: ReactNode;
    areas: readonly ShellFocusArea[];
    pathname: string;
    footer?: ReactNode;
    onNavigate?: () => void;
    /** Router link component; defaults to a plain anchor. */
    link?: ShellLink;
    /** The area tab strip. Off, every area's links are listed under a heading — right for apps with only a couple of areas. */
    switcher?: boolean;
    /** Rendered at the top of the scrolling navigation, before the areas (a project switcher, say). */
    lead?: ReactNode;
}) {
    const matchingArea = useMemo(
        () => areas.find(area => area.items.some(item => isPathActive(pathname, item.to)))?.id,
        [areas, pathname],
    );
    const [activeArea, setActiveArea] = useState(matchingArea ?? areas[0]?.id ?? "");
    useEffect(() => { if (matchingArea) setActiveArea(matchingArea); }, [matchingArea]);
    const area = areas.find(candidate => candidate.id === activeArea) ?? areas[0];

    const renderArea = (target: ShellFocusArea) => (
        <>
            {target.action && (() => {
                const ActionIcon = target.action.icon;
                return (
                    <div className="focus-area-command">
                        <Link
                            to={target.action.to}
                            state={target.action.state}
                            onClick={onNavigate}
                            className="focus-area-command-button"
                        >
                            <ActionIcon aria-hidden="true" />
                            <span>{target.action.label}</span>
                        </Link>
                    </div>
                );
            })()}
            <nav aria-label={`${target.label} navigation`}>
                {target.items.map(item => {
                    const Icon = item.icon;
                    const active = isPathActive(pathname, item.to);
                    return (
                        <Link key={item.to} to={item.to} onClick={onNavigate} className={`focus-nav-link${active ? " active" : ""}`} aria-current={active ? "page" : undefined}>
                            <Icon aria-hidden="true" /><span>{item.label}</span>{item.count != null && item.count > 0 && <small className="focus-nav-count">{item.count > 99 ? "99+" : item.count}</small>}
                        </Link>
                    );
                })}
            </nav>
        </>
    );

    if (!switcher) {
        return (
            <div className="focus-sidebar-inner">
                <div className="focus-brand">{brand}</div>
                <section className="focus-area-panel">
                    {lead}
                    {areas.map(target => (
                        <div key={target.id} className="focus-area-group">
                            <h2 className="focus-area-label">{target.label}</h2>
                            {renderArea(target)}
                        </div>
                    ))}
                </section>
                {footer && <footer className="focus-sidebar-footer">{footer}</footer>}
            </div>
        );
    }

    return (
        <div className="focus-sidebar-inner">
            <div className="focus-brand">{brand}</div>
            <div className="focus-switcher" role="tablist" aria-label="Focus areas">
                {areas.map(candidate => {
                    const Icon = candidate.icon;
                    return (
                        <button
                            key={candidate.id}
                            type="button"
                            role="tab"
                            aria-selected={candidate.id === area?.id}
                            className={candidate.id === area?.id ? "active" : ""}
                            onClick={() => setActiveArea(candidate.id)}
                            title={candidate.label}
                        >
                            <Icon aria-hidden="true" /><span>{candidate.label}</span>
                        </button>
                    );
                })}
            </div>
            {area && <section className="focus-area-panel">{lead}{renderArea(area)}</section>}
            {footer && <footer className="focus-sidebar-footer">{footer}</footer>}
        </div>
    );
}
